import { useEffect, useMemo, useState } from "react";
import { ProductLibProcessJob } from "../../API";
import { format } from "date-fns";
import DataGrid, { Column, SortColumn } from "react-data-grid";
import EmptyRowsRenderer from "../../components/EmptyRow";
import LoadingRowsRenderer from "../../components/Loading";
import {
  getComparator,
  dataGridTabClassname,
  styleDataGrid,
  filterColumnClassName,
  filterCellClass,
} from "../../datagrid/extra";
import { AiOutlineReload } from "react-icons/ai";
import { HiOutlinePlus, HiOutlineTrash, HiOutlineClipboardCopy, HiOutlineCheckCircle } from "react-icons/hi";
import { fetchProcessJobsByJobType, createProcessJob, deleteProcessJob, updateProcessJob, JobStatus, JobType, IsActive } from "../../apis/job";
import ModalConfirm from "../../components/ModalConfirm";

interface SummaryRow {
  id: string;
  totalCount: number;
}

function ControlPanelCreateProductKnowledge() {
  const [rows, setRows] = useState<ProductLibProcessJob[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [sortColumns, setSortColumns] = useState<readonly SortColumn[]>([
    { columnKey: "updatedAt", direction: "DESC" },
  ]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  const [showActiveConfirm, setShowActiveConfirm] = useState(false);
  const [selectedActiveJobId, setSelectedActiveJobId] = useState<string | null>(null);

  const dateFormat = "dd-MM-yyyy (HH:mm:ss)";

  const sortedRows = useMemo((): readonly ProductLibProcessJob[] => {
    if (sortColumns.length === 0) return rows;

    return [...rows].sort((a, b) => {
      for (const sort of sortColumns) {
        const comparator = getComparator(sort.columnKey);
        const compResult = comparator(a, b);
        if (compResult !== 0) {
          return sort.direction === "ASC" ? compResult : -compResult;
        }
      }
      return 0;
    });
  }, [rows, sortColumns]);

  const summaryRows = useMemo(() => {
    const summaryRow: SummaryRow = {
      id: "total_0",
      totalCount: sortedRows.length,
    };
    return [summaryRow];
  }, [sortedRows]);

  const hasProcessingJob = useMemo(() => {
    return rows.some((job) => job.status === JobStatus.PROCESSING);
  }, [rows]);

  const loadProcessJobs = async () => {
    setIsLoading(true);
    try {
      const jobs = await fetchProcessJobsByJobType(JobType.PRODUCT_KNOWLEDGE);
      setRows(jobs);
    } catch (error) {
      console.error("Error fetching process jobs:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateProductKnowledge = async () => {
    setIsLoading(true);
    try {
      const createdDate = format(new Date(), "yyyyMMdd");
      await createProcessJob({
        jobType: JobType.PRODUCT_KNOWLEDGE,
        status: JobStatus.PROCESSING,
        createdDate,
      });
      await loadProcessJobs();
    } catch (error) {
      console.error("Error creating process job:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteClick = (jobId: string) => {
    setSelectedJobId(jobId);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedJobId) return;
    setIsLoading(true);
    try {
      await deleteProcessJob(selectedJobId);
      setShowDeleteConfirm(false);
      setSelectedJobId(null);
      await loadProcessJobs();
    } catch (error) {
      console.error("Error deleting process job:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleActiveClick = (jobId: string) => {
    setSelectedActiveJobId(jobId);
    setShowActiveConfirm(true);
  };

  const handleActiveConfirm = async () => {
    if (!selectedActiveJobId) return;
    setIsLoading(true);
    try {
      // Deactivate all other jobs with same jobType first
      const activeJobs = rows.filter(
        (job) => job.isActive === IsActive.YES && job.id !== selectedActiveJobId
      );
      for (const job of activeJobs) {
        await updateProcessJob({ id: job.id, isActive: IsActive.NO });
      }
      // Activate the selected job
      await updateProcessJob({ id: selectedActiveJobId, isActive: IsActive.YES });
      setShowActiveConfirm(false);
      setSelectedActiveJobId(null);
      await loadProcessJobs();
    } catch (error) {
      console.error("Error updating job active status:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const processJobColumns: Column<ProductLibProcessJob, SummaryRow>[] = [
    {
      key: "id",
      name: "ID",
      width: "20%",
      sortable: true,
      headerCellClass: filterColumnClassName,
      cellClass: filterCellClass,
      formatter: ({ row }) => {
        const shortId = row.id.substring(0, 8);
        const handleCopy = () => {
          navigator.clipboard.writeText(row.id);
        };
        return (
          <div className="flex items-center gap-2">
            <span className="font-mono">{shortId}...</span>
            <button
              type="button"
              className="p-1 text-gray-500 hover:text-violet-500 hover:bg-gray-100 rounded"
              onClick={handleCopy}
              title="Copy full ID"
            >
              <HiOutlineClipboardCopy size={16} />
            </button>
          </div>
        );
      },
      summaryFormatter({ row }) {
        return (
          <div className="opacity-50">
            <strong>({row.totalCount} Rows)</strong>
          </div>
        );
      },
    },
    {
      key: "status",
      name: "Status",
      width: "12%",
      sortable: true,
      headerCellClass: filterColumnClassName,
      cellClass: filterCellClass,
      formatter: ({ row }) => {
        const statusColor =
          row.status === JobStatus.COMPLETED
            ? "bg-green-100 text-green-800"
            : row.status === JobStatus.PROCESSING
            ? "bg-yellow-100 text-yellow-800"
            : row.status === JobStatus.FAILED
            ? "bg-red-100 text-red-800"
            : "bg-gray-100 text-gray-800";

        return (
          <div className="flex items-center justify-center h-full">
            <span
              className={`px-2 py-1 text-xs font-semibold rounded-full ${statusColor}`}
            >
              {row.status}
            </span>
          </div>
        );
      },
    },
    {
      key: "isActive",
      name: "Active",
      width: "10%",
      sortable: true,
      headerCellClass: filterColumnClassName,
      cellClass: filterCellClass,
      formatter: ({ row }) => {
        const isActiveColor =
          row.isActive === IsActive.YES
            ? "bg-green-100 text-green-800"
            : "bg-gray-100 text-gray-800 hover:bg-green-50 hover:text-green-600 cursor-pointer";

        return (
          <div className="flex items-center justify-center h-full">
            <span
              className={`px-2 py-1 text-xs font-semibold rounded-full ${isActiveColor}`}
              onClick={() => {
                if (row.isActive !== IsActive.YES) {
                  handleActiveClick(row.id);
                }
              }}
              title={row.isActive === IsActive.YES ? "Currently active" : "Click to set as active"}
            >
              {row.isActive === IsActive.YES ? "Yes" : "No"}
            </span>
          </div>
        );
      },
    },
    {
      key: "createdAt",
      name: "Created At",
      width: "18%",
      sortable: true,
      headerCellClass: filterColumnClassName,
      cellClass: filterCellClass,
      formatter: ({ row }) => (
        <div className="items-center">
          {format(new Date(row.createdAt), dateFormat)}
        </div>
      ),
    },
    {
      key: "updatedAt",
      name: "Updated At",
      width: "18%",
      sortable: true,
      headerCellClass: filterColumnClassName,
      cellClass: filterCellClass,
      formatter: ({ row }) => (
        <div className="items-center">
          {format(new Date(row.updatedAt), dateFormat)}
        </div>
      ),
    },
    {
      key: "action",
      name: "Action",
      width: "24%",
      headerCellClass: filterColumnClassName,
      cellClass: filterCellClass,
      formatter: ({ row }) => (
        <div className="flex items-center justify-center h-full">
          {row.status === JobStatus.PROCESSING && (
            <button
              type="button"
              className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-white bg-red-500 rounded hover:bg-red-600"
              onClick={() => handleDeleteClick(row.id)}
            >
              <HiOutlineTrash size={14} />
              Delete
            </button>
          )}
        </div>
      ),
    },
  ];

  useEffect(() => {
    loadProcessJobs();
  }, []);

  return (
    <>
      <ModalConfirm
        show={showDeleteConfirm}
        onHide={() => setShowDeleteConfirm(false)}
        title="Delete Job Confirmation"
        cancelText="Cancel"
        cancelAction={() => {
          setShowDeleteConfirm(false);
          setSelectedJobId(null);
        }}
        okText="Delete"
        okAction={handleDeleteConfirm}
        okClass="justify-center px-4 py-2 text-md font-medium text-white bg-red-500 rounded hover:bg-red-600"
        loading={isLoading}
      >
        <div className="flex flex-col items-center justify-center mb-2 space-y-2 text-center">
          <HiOutlineTrash size={36} className="text-red-500" />
          <p>Are you sure you want to delete this job?</p>
          <p className="text-sm text-gray-500">This action cannot be undone.</p>
        </div>
      </ModalConfirm>

      <ModalConfirm
        show={showActiveConfirm}
        onHide={() => setShowActiveConfirm(false)}
        title="Set Active Job Confirmation"
        cancelText="Cancel"
        cancelAction={() => {
          setShowActiveConfirm(false);
          setSelectedActiveJobId(null);
        }}
        okText="Set Active"
        okAction={handleActiveConfirm}
        okClass="justify-center px-4 py-2 text-md font-medium text-white bg-blue-500 rounded hover:bg-blue-600"
        loading={isLoading}
      >
        <div className="flex flex-col items-center justify-center mb-2 space-y-2 text-center">
          <HiOutlineCheckCircle size={36} className="text-blue-500" />
          <p>Do you want to set this job as active?</p>
          <p className="text-sm text-gray-500">Other active jobs will be deactivated.</p>
        </div>
      </ModalConfirm>

      <div>
        <div className="page-container-title">
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-semibold">Product Knowledge Jobs</h2>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-lg focus:outline-none focus:ring-2 ${
                hasProcessingJob
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-violet-500 hover:bg-violet-600 focus:ring-violet-300"
              }`}
              onClick={() => handleCreateProductKnowledge()}
              disabled={isLoading || hasProcessingJob}
              title={hasProcessingJob ? "A job is already processing" : "Create new product knowledge job"}
            >
              <HiOutlinePlus size={18} />
              Create Product Knowledge
            </button>
            <button
              type="button"
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-emerald-500 rounded-lg hover:bg-emerald-600 focus:outline-none focus:ring-2 focus:ring-emerald-300"
              onClick={() => loadProcessJobs()}
              disabled={isLoading}
            >
              <AiOutlineReload size={18} className={isLoading ? "animate-spin" : ""} />
              Refresh
            </button>
          </div>
        </div>

        <DataGrid
          columns={processJobColumns}
          rows={sortedRows}
          bottomSummaryRows={summaryRows}
          renderers={{ noRowsFallback: <EmptyRowsRenderer /> }}
          style={{ height: "60vh" }}
          rowHeight={42}
          headerRowHeight={50}
          sortColumns={sortColumns}
          onSortColumnsChange={setSortColumns}
          className={dataGridTabClassname}
        />
        <div className="loading-renderer">
          {isLoading && <LoadingRowsRenderer />}
        </div>
        <style>{styleDataGrid}</style>
        <style>{`
          .rdg-header-sort-cell {
            display: flex;
            align-items: center;
            justify-content: space-between;
          }
          .rdg-header-sort-name {
            flex: 1;
          }
        `}</style>
      </div>
    </>
  );
}

export default ControlPanelCreateProductKnowledge;
